import React, { useState, useEffect, useMemo } from 'react';
import {
    Container, Typography, Box, Paper, CircularProgress, Grid, FormControl, InputLabel, Select,
    MenuItem, Alert, IconButton, SelectChangeEvent, Checkbox, ListItemText, OutlinedInput, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';

// --- Tipos e Interfaces ---
interface DadosOriginais {
    MES: string;
    VENDEDOR: string;
    FATURAMENTO_REAL: number;
    FATURAMENTO_OBJ: number;
    MARGEM_REAL: number;
    MARGEM_OBJ: number;
    CLIENTES_REAL: number;
    CLIENTES_OBJ: number;
    PESO_M3_REAL: number;
    PESO_M3_OBJ: number;
    PREMIACAO: number;
}

interface DadosClientes {
    nome: string;
    mes: string;
    vendedor: string;
    valorVendido: number;
    status: string;
}

interface DadosProdutos {
    mes: string;
    nomeProduto: string;
    nomeCliente: string;
    m3: number;
    quantidade: number;
    total: number;
    custo: number;
    lucroBruto: number;
    nomeVendedor: string;
    grupos: string;
    subGrupo: string;
}

interface KpiData {
    faturamentoReal: number;
    faturamentoObj: number;
}

interface TopProduto {
    nome: string;
    total: number;
}

interface UnidadeData {
    unidade: string;
    kpis: KpiData;
    totalClientes: number;
    top10Produtos: TopProduto[];
}

// --- Funções de Formatação ---
const formatarMoeda = (v: number) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
const calcularAtingimento = (real: number, obj: number) => obj > 0 ? (real / obj) * 100 : 0;

const VisaoGeralPage: React.FC = () => {
    const [todasUnidades, setTodasUnidades] = useState<string[]>([]);
    const [dadosConsolidados, setDadosConsolidados] = useState<{[unidade: string]: {main: DadosOriginais[], clients: DadosClientes[], products: DadosProdutos[]}}>({});
    const [mesesDisponiveis, setMesesDisponiveis] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [unidadesSelecionadas, setUnidadesSelecionadas] = useState<string[]>(['Todos']);
    const [mesesSelecionados, setMesesSelecionados] = useState<string[]>(['Todos']);
    const navigate = useNavigate();

    // Buscar unidades disponíveis
    useEffect(() => {
        const fetchUnidades = async () => {
            const token = localStorage.getItem('authToken');
            try {
                const response = await fetch('/api/unidades', { headers: { 'Authorization': `Bearer ${token}` } });
                if (!response.ok) throw new Error('Falha ao buscar unidades.');
                const data = await response.json();
                setTodasUnidades(data);
                return data;
            } catch (err: any) {
                setError(err.message);
                return [];
            }
        };
        fetchUnidades();
    }, []);

    // Buscar dados de todas as unidades (estratégia similar à CorridaPage)
    useEffect(() => {
        const fetchAllData = async () => {
            if (todasUnidades.length === 0) return;
            
            setLoading(true);
            setError(null);
            const token = localStorage.getItem('authToken');
            if (!token) { navigate('/login'); return; }

            try {
                const dadosTodasUnidades: {[unidade: string]: {main: DadosOriginais[], clients: DadosClientes[], products: DadosProdutos[]}} = {};
                const todosMesesComDados = new Set<string>();

                // Buscar dados de cada unidade sequencialmente (evita sobrecarga)
                for (const unidade of todasUnidades) {
                    try {
                        // Buscar dados principais
                        const responseMain = await fetch(`/api/dados?unidade=${encodeURIComponent(unidade)}&tipo=main`, { headers: { 'Authorization': `Bearer ${token}` } });
                        const dadosMain: DadosOriginais[] = responseMain.ok ? await responseMain.json() : [];

                        // Buscar dados de clientes
                        const responseClients = await fetch(`/api/dados?unidade=${encodeURIComponent(unidade)}&tipo=clients`, { headers: { 'Authorization': `Bearer ${token}` } });
                        const dadosClients: DadosClientes[] = responseClients.ok ? await responseClients.json() : [];

                        // Buscar dados de produtos
                        const responseProducts = await fetch(`/api/dados?unidade=${encodeURIComponent(unidade)}&tipo=products`, { headers: { 'Authorization': `Bearer ${token}` } });
                        const dadosProducts: DadosProdutos[] = responseProducts.ok ? await responseProducts.json() : [];

                        dadosTodasUnidades[unidade] = {
                            main: dadosMain,
                            clients: dadosClients,
                            products: dadosProducts
                        };

                        // Coletar meses disponíveis
                        dadosMain.forEach(row => {
                            if (row.MES) {
                                // Converter de maiúsculo para formato amigável
                                const mesFormatado = row.MES.charAt(0).toUpperCase() + row.MES.slice(1).toLowerCase();
                                todosMesesComDados.add(mesFormatado);
                            }
                        });

                    } catch (unitError) {
                        console.error(`Erro ao buscar dados da unidade ${unidade}:`, unitError);
                        // Continua com outras unidades mesmo se uma falhar
                        dadosTodasUnidades[unidade] = { main: [], clients: [], products: [] };
                    }
                }

                setDadosConsolidados(dadosTodasUnidades);

                // Ordenar meses
                const ordemMeses = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
                const mesesOrdenados = Array.from(todosMesesComDados).sort((a, b) => ordemMeses.indexOf(a) - ordemMeses.indexOf(b));
                setMesesDisponiveis(mesesOrdenados);

            } catch (err: any) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAllData();
    }, [todasUnidades, navigate]);

    // Função de filtro corrigida
    const handleFilterChange = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (event: SelectChangeEvent<string[]>) => {
        const { value } = event.target;
        let valorArray = typeof value === 'string' ? value.split(',') : value;

        // Se selecionou "Todos", zera os outros
        if (valorArray.includes('Todos')) {
            setter(['Todos']);
        } else {
            // Remove "Todos" se outro valor foi selecionado
            valorArray = valorArray.filter(v => v !== 'Todos');
            setter(valorArray);
        }
    };

    // Processar dados filtrados (similar à lógica da CorridaPage)
    const dadosProcessados = useMemo(() => {
        const resultados: UnidadeData[] = [];

        Object.entries(dadosConsolidados).forEach(([unidade, dados]) => {
            // Filtrar por unidade
            if (unidadesSelecionadas.length > 0 && !unidadesSelecionadas.includes('Todos')) {
                if (!unidadesSelecionadas.includes(unidade)) return;
            }

            // Filtrar dados principais por mês
            const dadosMainFiltrados = dados.main.filter(row => {
                if (mesesSelecionados.includes('Todos')) return true;
                if (!row.MES) return false;
                const mesFormatado = row.MES.charAt(0).toUpperCase() + row.MES.slice(1).toLowerCase();
                return mesesSelecionados.includes(mesFormatado);
            });

            // Filtrar dados de clientes por mês
            const dadosClientsFiltrados = dados.clients.filter(row => {
                if (mesesSelecionados.includes('Todos')) return true;
                if (!row.mes) return false;
                const mesFormatado = row.mes.charAt(0).toUpperCase() + row.mes.slice(1).toLowerCase();
                return mesesSelecionados.includes(mesFormatado);
            });

            // Filtrar dados de produtos por mês
            const dadosProductsFiltrados = dados.products.filter(row => {
                if (mesesSelecionados.includes('Todos')) return true;
                if (!row.mes) return false;
                const mesFormatado = row.mes.charAt(0).toUpperCase() + row.mes.slice(1).toLowerCase();
                return mesesSelecionados.includes(mesFormatado);
            });

            // Calcular KPIs
            const faturamentoReal = dadosMainFiltrados.reduce((sum, row) => sum + (row.FATURAMENTO_REAL || 0), 0);
            const faturamentoObj = dadosMainFiltrados.reduce((sum, row) => sum + (row.FATURAMENTO_OBJ || 0), 0);
            
            // Contar clientes únicos
            const totalClientes = new Set(dadosClientsFiltrados.map(c => c.nome)).size;

            // Calcular Top 10 Produtos por grupo
            const produtosAgrupados = dadosProductsFiltrados.reduce((acc, p) => {
                const grupo = p.grupos || 'Outros';
                acc[grupo] = (acc[grupo] || 0) + (p.total || 0);
                return acc;
            }, {} as {[key: string]: number});

            const top10Produtos = Object.entries(produtosAgrupados)
                .sort(([, totalA], [, totalB]) => totalB - totalA)
                .slice(0, 10)
                .map(([nome, total]) => ({ nome, total }));

            resultados.push({
                unidade,
                kpis: { faturamentoReal, faturamentoObj },
                totalClientes,
                top10Produtos,
            });
        });

        return resultados;
    }, [dadosConsolidados, unidadesSelecionadas, mesesSelecionados]);

    // Dados para o gráfico de faturamento consolidado
    const dadosGraficoFaturamento = useMemo(() => {
        return dadosProcessados.map(d => ({
            name: d.unidade,
            Real: d.kpis.faturamentoReal,
            Objetivo: d.kpis.faturamentoObj
        }));
    }, [dadosProcessados]);

    // --- INÍCIO DAS NOVAS FUNÇÕES ---
    // Componente para renderizar o rótulo da barra de Objetivo
    const renderObjetivoLabel = (props: any) => {
        const { x, y, width, value } = props;
        if (value === 0) return null;
        return (
            <text x={x + width / 2} y={y} dy={-4} fill="#666" textAnchor="middle" fontSize={12}>
                {formatarMoeda(value)}
            </text>
        );
    };
    
    // Componente para renderizar o rótulo da barra Real com o percentual de atingimento
    const renderRealLabel = (props: any) => {
        const { x, y, width, index, value } = props;
        const dataPoint = dadosGraficoFaturamento[index];
        if (!dataPoint || value === 0) return null;
    
        const realValue = dataPoint.Real;
        const objetivoValue = dataPoint.Objetivo;
        const atingimento = calcularAtingimento(realValue, objetivoValue);
    
        let corAtingimento = '#d32f2f'; // Vermelho (padrão < 90%)
        if (atingimento >= 100) {
            corAtingimento = '#2e7d32'; // Verde (>= 100%)
        } else if (atingimento >= 90) { 
            corAtingimento = '#fbc02d'; // Amarelo (90% a 99.9%)
        }
    
        const xPos = x + width / 2;
    
        return (
            <g transform={`translate(${xPos}, ${y})`}>
                <text x={0} y={0} dy={-20} fill="#333" textAnchor="middle" fontSize={12}>
                    {formatarMoeda(value)}
                </text>
                <text x={0} y={0} dy={-5} fill={corAtingimento} textAnchor="middle" fontSize={12} fontWeight="bold">
                    {`(${atingimento.toFixed(1)}%)`}
                </text>
            </g>
        );
    };
    // --- FIM DAS NOVAS FUNÇÕES ---

    if (error) return <Alert severity="error" sx={{ m: 4 }}>{error}</Alert>;

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <IconButton component={RouterLink} to="/" aria-label="voltar"><ArrowBackIcon /></IconButton>
                <Typography variant="h4" component="h1" sx={{ ml: 1 }}>Visão Geral do Grupo</Typography>
            </Box>

            {/* Filtros */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Unidades</InputLabel>
                            <Select multiple value={unidadesSelecionadas} onChange={handleFilterChange(setUnidadesSelecionadas)} input={<OutlinedInput label="Unidades" />} renderValue={(s) => s.join(', ')}>
                                <MenuItem value="Todos"><Checkbox checked={unidadesSelecionadas.includes('Todos')} /><ListItemText primary="Todas as Unidades" /></MenuItem>
                                {todasUnidades.map(u => <MenuItem key={u} value={u}><Checkbox checked={unidadesSelecionadas.indexOf(u) > -1} /><ListItemText primary={u} /></MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Meses</InputLabel>
                            <Select multiple value={mesesSelecionados} onChange={handleFilterChange(setMesesSelecionados)} input={<OutlinedInput label="Meses" />} renderValue={(s) => s.join(', ')}>
                                <MenuItem value="Todos"><Checkbox checked={mesesSelecionados.includes('Todos')} /><ListItemText primary="Todos os Meses" /></MenuItem>
                                {mesesDisponiveis.map(m => <MenuItem key={m} value={m}><Checkbox checked={mesesSelecionados.indexOf(m) > -1} /><ListItemText primary={m} /></MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress /></Box> : (
            <Grid container spacing={3}>
                {/* Gráfico de Faturamento com ajustes */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, height: 400 }}>
                        <Typography variant="h6" gutterBottom>Faturamento por Unidade (Real vs. Objetivo)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={dadosGraficoFaturamento} margin={{ top: 35, right: 20, left: 20, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis tickFormatter={(value) => formatarMoeda(value)} />
                                <Tooltip formatter={(value: any) => formatarMoeda(value as number)} />
                                <Legend />
                                <Bar dataKey="Objetivo" fill="#bdbdbd" name="Objetivo">
                                    <LabelList dataKey="Objetivo" content={renderObjetivoLabel} />
                                </Bar>
                                <Bar dataKey="Real" fill="#1a237e" name="Real">
                                    <LabelList dataKey="Real" content={renderRealLabel} />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Resumo de Clientes e Produtos */}
                {dadosProcessados.map(d => (
                    <Grid item xs={12} md={6} key={d.unidade}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="h6" gutterBottom>{d.unidade}</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                <strong>Faturamento Real:</strong> {formatarMoeda(d.kpis.faturamentoReal)}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                <strong>Faturamento Objetivo:</strong> {formatarMoeda(d.kpis.faturamentoObj)}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                <strong>Atingimento:</strong> {calcularAtingimento(d.kpis.faturamentoReal, d.kpis.faturamentoObj).toFixed(1)}%
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                <strong>Clientes Atendidos:</strong> {d.totalClientes}
                            </Typography>
                            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Top 10 Grupos de Produtos</Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Grupo</TableCell>
                                            <TableCell align="right">Total Vendido</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {d.top10Produtos.map((p, index) => (
                                            <TableRow key={`${p.nome}-${index}`}>
                                                <TableCell>{p.nome}</TableCell>
                                                <TableCell align="right">{formatarMoeda(p.total)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                ))}
            </Grid>
            )}
        </Container>
    );
};

export default VisaoGeralPage;
